// Gestión de carrito temporal para usuarios no logueados
const TEMP_CART_KEY = 'temp-cart-minibid24hs';
const TEMP_CART_STORE_ID = '30e79df7-81e8-4c5f-a825-602e5899d961';

export interface TempCartItem {
  product_id: string;
  quantity: number;
  unit_price: number;
  title: string;
}

export interface TempCart {
  store_id: string;
  items: TempCartItem[];
  created_at: string;
}

/**
 * Obtiene el carrito temporal del localStorage
 */
export const getTempCart = (storeId?: string): TempCart | null => {
  try {
    const stored = localStorage.getItem(TEMP_CART_KEY);
    const cart = stored ? JSON.parse(stored) : null;
    if (!cart) return null;
    if (storeId && cart.store_id !== storeId) return null;
    return cart;
  } catch (e) {
    console.warn('[TempCart] Error reading temp cart:', e);
    return null;
  }
};

/**
 * Guarda el carrito temporal en localStorage
 */
export const saveTempCart = (cart: TempCart): void => {
  try {
    localStorage.setItem(TEMP_CART_KEY, JSON.stringify(cart));
  } catch (e) {
    console.warn('[TempCart] Error saving temp cart:', e);
  }
};

/**
 * Agrega un item al carrito temporal
 */
export const addToTempCart = (
  product: { id: string; title: string; fixed_sale_price: number; currency?: string; stock?: number },
  quantity: number = 1,
  storeId?: string
): void => {
  const targetStoreId = storeId || TEMP_CART_STORE_ID;
  let cart = getTempCart();

  if (cart && cart.store_id !== targetStoreId) {
    cart = null;
  }

  if (!cart) {
    cart = {
      store_id: targetStoreId,
      items: [],
      created_at: new Date().toISOString(),
    };
  }

  const existingItem = cart.items.find((item) => item.product_id === product.id);

  if (existingItem) {
    existingItem.quantity += quantity;
  } else {
    cart.items.push({
      product_id: product.id,
      quantity: quantity,
      unit_price: product.fixed_sale_price,
      title: product.title,
    });
  }

  saveTempCart(cart);
};

/**
 * Actualiza la cantidad de un item en el carrito temporal
 */
export const updateTempCartItemQuantity = (productId: string, newQuantity: number): void => {
  const cart = getTempCart();
  if (!cart) return;

  if (newQuantity <= 0) {
    cart.items = cart.items.filter((item) => item.product_id !== productId);
  } else {
    const item = cart.items.find((item) => item.product_id === productId);
    if (item) {
      item.quantity = newQuantity;
    }
  }

  saveTempCart(cart);
};

/**
 * Elimina un item del carrito temporal
 */
export const removeFromTempCart = (productId: string): void => {
  const cart = getTempCart();
  if (!cart) return;

  cart.items = cart.items.filter((item) => item.product_id !== productId);
  saveTempCart(cart);
};

/**
 * Limpia el carrito temporal
 */
export const clearTempCart = (): void => {
  try {
    localStorage.removeItem(TEMP_CART_KEY);
  } catch (e) {
    console.warn('[TempCart] Error clearing temp cart:', e);
  }
};

/**
 * Convierte items del carrito temporal a items del carrito real
 */
export const getTempCartAsItems = (storeId?: string): any[] => {
  const cart = getTempCart(storeId);
  if (!cart) return [];

  return cart.items.map((item) => ({
    id: `temp-${item.product_id}`,
    product_id: item.product_id,
    quantity: item.quantity,
    unit_price: item.unit_price,
    // Normalizar estructura para que sea consistente con items de BD
    products: {
      id: item.product_id,
      title: item.title,
      fixed_sale_price: item.unit_price,
    },
  }));
};
